import { UrlApi } from "../../../services/apirest";

export const normalizarCuit = (cuit) => String(cuit || "").replace(/\D/g, "");

export const getEmpresas = async () => {
  const res = await fetch(`${UrlApi}/empresas`);
  const data = await res.json();

  if (!res.ok || data?.ok === false) {
    throw new Error(
      data?.message || data?.error || "No se pudieron obtener las empresas"
    );
  }

  return Array.isArray(data) ? data : data?.empresas ?? [];
};

export const getImputacionesGastos = async () => {
  const res = await fetch(`${UrlApi}/imputacion_gastos`);
  const data = await res.json();

  if (!res.ok || data?.ok === false) {
    throw new Error(
      data?.message || data?.error || "No se pudieron obtener las imputaciones"
    );
  }

  return Array.isArray(data) ? data : data?.imputaciones ?? [];
};

/**
 * GET /orden-pago/pendientes
 * Comprobantes con saldo pendiente, agrupados por proveedor.
 */
export const getPendientesEmpresa = async ({
  id_empresa,
  vto_desde,
  vto_hasta,
  cuit,
  id_imputacion,
  limit,
} = {}) => {
  if (id_empresa == null || id_empresa === "") {
    throw new Error("id_empresa es obligatorio");
  }

  const params = new URLSearchParams();
  params.set("id_empresa", String(id_empresa));
  if (vto_desde) params.set("vto_desde", String(vto_desde));
  if (vto_hasta) params.set("vto_hasta", String(vto_hasta));
  if (cuit) {
    const cuitNorm = normalizarCuit(cuit);
    if (cuitNorm) params.set("cuit", cuitNorm);
  }
  if (id_imputacion != null && id_imputacion !== "") {
    params.set("id_imputacion", String(id_imputacion));
  }
  if (limit != null) params.set("limit", String(limit));

  const res = await fetch(
    `${UrlApi}/orden-pago/pendientes?${params.toString()}`
  );
  const data = await res.json();

  if (!res.ok || data.ok === false) {
    throw new Error(
      data.msg ||
        data.message ||
        data.error ||
        "No se pudieron obtener los comprobantes pendientes"
    );
  }

  return data;
};

const buildPendientesExportParams = ({
  id_empresa,
  vto_desde,
  vto_hasta,
  cuit,
  id_imputacion,
} = {}) => {
  if (id_empresa == null || id_empresa === "") {
    throw new Error("id_empresa es obligatorio");
  }

  const params = new URLSearchParams();
  params.set("id_empresa", String(id_empresa));
  if (vto_desde) params.set("vto_desde", String(vto_desde));
  if (vto_hasta) params.set("vto_hasta", String(vto_hasta));
  if (cuit) {
    const cuitNorm = normalizarCuit(cuit);
    if (cuitNorm) params.set("cuit", cuitNorm);
  }
  if (id_imputacion != null && id_imputacion !== "") {
    params.set("id_imputacion", String(id_imputacion));
  }
  return params;
};

const leerErrorRespuestaBlob = async (res, fallback) => {
  try {
    const data = await res.json();
    return data?.msg || data?.message || data?.error || fallback;
  } catch {
    return fallback;
  }
};

/**
 * GET /orden-pago/pendientes/export
 * PDF de todos los pendientes (respeta filtros de pantalla).
 * Por proveedor: datos + tabla de comprobantes.
 */
export const exportarPendientesEmpresaTodo = async (filtros = {}) => {
  const params = buildPendientesExportParams(filtros);
  const res = await fetch(
    `${UrlApi}/orden-pago/pendientes/export?${params.toString()}`
  );

  if (!res.ok) {
    throw new Error(
      await leerErrorRespuestaBlob(
        res,
        "No se pudo exportar el listado de pendientes"
      )
    );
  }

  const blob = await res.blob();
  const filename = parseFilenameDescarga(
    res.headers.get("Content-Disposition"),
    "pagos_proveedores_pendientes_todos.pdf"
  );
  return { blob, filename };
};

/**
 * POST /orden-pago/pendientes/export
 * PDF de proveedores/comprobantes seleccionados.
 * Body: { ids?: number[], cuits?: string[] }
 */
export const exportarPendientesEmpresaSeleccionados = async (
  filtros = {},
  { ids = [], cuits = [] } = {}
) => {
  const params = buildPendientesExportParams(filtros);

  const idsNorm = (Array.isArray(ids) ? ids : [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
  const cuitsNorm = (Array.isArray(cuits) ? cuits : [])
    .map((c) => normalizarCuit(c))
    .filter((c) => c.length > 0);

  if (!idsNorm.length && !cuitsNorm.length) {
    throw new Error("Seleccione al menos un proveedor para exportar");
  }

  const body = {};
  if (idsNorm.length) body.ids = idsNorm;
  if (cuitsNorm.length) body.cuits = cuitsNorm;

  const res = await fetch(
    `${UrlApi}/orden-pago/pendientes/export?${params.toString()}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );

  if (!res.ok) {
    throw new Error(
      await leerErrorRespuestaBlob(
        res,
        "No se pudo exportar los proveedores seleccionados"
      )
    );
  }

  const blob = await res.blob();
  const filename = parseFilenameDescarga(
    res.headers.get("Content-Disposition"),
    "pagos_proveedores_pendientes_seleccionados.pdf"
  );
  return { blob, filename };
};

export const getPendientesPorCuit = async (cuit, { id_empresa } = {}) => {
  const cuitNorm = normalizarCuit(cuit);
  if (!cuitNorm) {
    throw new Error("Ingrese un CUIT válido");
  }

  const params = new URLSearchParams();
  if (id_empresa != null && id_empresa !== "") {
    params.set("id_empresa", String(id_empresa));
  }
  const query = params.toString();
  const url = query
    ? `${UrlApi}/orden-pago/proveedores/${cuitNorm}/pendientes?${query}`
    : `${UrlApi}/orden-pago/proveedores/${cuitNorm}/pendientes`;

  const res = await fetch(url);
  const data = await res.json();

  if (!res.ok || data.ok === false) {
    throw new Error(
      data.message || data.error || "No se pudieron obtener los comprobantes pendientes"
    );
  }

  return data;
};

export const previewRetenciones = async (cuit, body) => {
  const cuitNorm = normalizarCuit(cuit);
  if (!cuitNorm) {
    throw new Error("Ingrese un CUIT válido");
  }

  const res = await fetch(
    `${UrlApi}/orden-pago/proveedores/${cuitNorm}/preview-retenciones`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const data = await res.json();

  if (!res.ok || data.ok === false) {
    throw new Error(
      data.msg || data.message || data.error || "No se pudo calcular el preview de retenciones"
    );
  }

  return data;
};

export const generarOrdenPago = async (cuit, body) => {
  const cuitNorm = normalizarCuit(cuit);
  if (!cuitNorm) {
    throw new Error("Ingrese un CUIT válido");
  }

  const res = await fetch(
    `${UrlApi}/orden-pago/proveedores/${cuitNorm}/generar`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const data = await res.json();

  if (!res.ok || data.ok === false) {
    const error = new Error(
      data.msg || data.message || data.error || "No se pudo generar la orden de pago"
    );
    // El 409 con codigo se resuelve reenviando con confirmar_advertencias
    error.status = res.status;
    error.codigo = data.codigo ?? null;
    error.advertencias = Array.isArray(data.advertencias) ? data.advertencias : [];
    error.resumen = data.resumen ?? null;
    throw error;
  }

  return data;
};

/**
 * PUT /api/orden-pago/:id
 * Actualiza OP en pendiente_aprobacion (reemplazo completo, body como generar).
 * Errores útiles: estado_invalido, concepto_no_editable, retencion_excede_pago (409).
 */
export const actualizarOrdenPago = async (idOrdenPago, body) => {
  const id = Number(idOrdenPago);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("id de orden de pago inválido");
  }

  const res = await fetch(`${UrlApi}/orden-pago/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  const data = await res.json().catch(() => ({}));

  if (!res.ok || data.ok === false) {
    const error = new Error(
      data.msg ||
        data.message ||
        data.error ||
        "No se pudo actualizar la orden de pago"
    );
    error.status = res.status;
    error.codigo = data.codigo ?? null;
    error.advertencias = Array.isArray(data.advertencias) ? data.advertencias : [];
    error.resumen = data.resumen ?? null;
    throw error;
  }

  return data;
};

/**
 * POST /orden-pago/proveedores/:cuit/adelanto
 * Crea OP con concepto=anticipo + registro en anticipos_proveedor.
 */
export const crearAdelantoProveedor = async (cuit, body) => {
  const cuitNorm = normalizarCuit(cuit);
  if (!cuitNorm) {
    throw new Error("Ingrese un CUIT válido");
  }

  const res = await fetch(
    `${UrlApi}/orden-pago/proveedores/${cuitNorm}/adelanto`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const data = await res.json();

  if (!res.ok || data.ok === false) {
    throw new Error(
      data.msg ||
        data.message ||
        data.error ||
        "No se pudo generar el adelanto al proveedor"
    );
  }

  return data;
};

/**
 * POST /orden-pago/lotes/preview
 * Valida medios por proveedor, cheques únicos y devuelve faltante/excedente por CUIT.
 * Body: { fecha_pago?, observaciones?, proveedores: [{ cuit, comprobantes, medios_pago, retenciones? }], pagos_no_proveedor? }
 */
export const previewLoteOrdenPago = async (body) => {
  const res = await fetch(`${UrlApi}/orden-pago/lotes/preview`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  const data = await res.json().catch(() => ({}));

  if (!res.ok || data.ok === false) {
    const error = new Error(
      data.msg || data.message || data.error || "No se pudo validar el preview del lote"
    );
    error.status = res.status;
    error.codigo = data.codigo ?? null;
    throw error;
  }

  return data;
};

/**
 * POST /orden-pago/lotes
 * Crea una OP por proveedor bajo una cabecera de lote, todo o nada.
 * Body: {
 *   user, fecha_pago, observaciones?,
 *   proveedores: [{ cuit, ...contrato del endpoint por CUIT }],
 *   pagos_no_proveedor?: [{ id_pago_no_proveedor }]
 * }
 * Errores: 409 retencion_excede_pago (confirmar_advertencias) y proveedor_error
 * con { indice, cuit } cuando falla un proveedor puntual.
 */
export const crearLoteOrdenPago = async (body) => {
  const res = await fetch(`${UrlApi}/orden-pago/lotes`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  const data = await res.json().catch(() => ({}));

  if (!res.ok || data.ok === false) {
    const error = new Error(
      data.msg || data.message || data.error || "No se pudo generar el lote de órdenes de pago"
    );
    error.status = res.status;
    error.codigo = data.codigo ?? null;
    error.advertencias = Array.isArray(data.advertencias) ? data.advertencias : [];
    error.resumen = data.resumen ?? null;
    error.proveedorError = data.proveedor_error ?? null;
    throw error;
  }

  return data;
};

/**
 * GET /orden-pago/lotes
 * Grilla de lotes con proveedores y totales.
 */
export const getLotesOrdenPago = async (params = {}) => {
  const q = new URLSearchParams();

  if (params.estado) q.set("estado", params.estado);
  const cuitFiltro = params.cuit || params.proveedor_cuit;
  if (cuitFiltro) {
    const cuitNorm = normalizarCuit(cuitFiltro);
    if (cuitNorm) q.set("cuit", cuitNorm);
  }
  if (params.fecha_desde) q.set("fecha_desde", params.fecha_desde);
  if (params.fecha_hasta) q.set("fecha_hasta", params.fecha_hasta);
  if (params.nro_lote) q.set("nro_lote", String(params.nro_lote).trim());
  if (params.id_empresa != null && params.id_empresa !== "") {
    q.set("id_empresa", String(params.id_empresa));
  }
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));

  const query = q.toString();
  const url = query
    ? `${UrlApi}/orden-pago/lotes?${query}`
    : `${UrlApi}/orden-pago/lotes`;
  const res = await fetch(url);
  const data = await res.json();

  if (!res.ok || data.ok === false) {
    throw new Error(
      data.msg || data.message || data.error || "No se pudieron obtener los lotes de órdenes de pago"
    );
  }

  return data;
};

/**
 * GET /orden-pago/lotes/:id
 * Pantalla de revisión: proveedores, comprobantes, retenciones y totales por medio.
 */
export const getLoteOrdenPagoById = async (idLote) => {
  const id = Number(idLote);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("id de lote inválido");
  }

  const res = await fetch(`${UrlApi}/orden-pago/lotes/${id}`);
  const data = await res.json();

  if (!res.ok || data.ok === false) {
    throw new Error(
      data.msg || data.message || data.error || "No se pudo obtener el lote de órdenes de pago"
    );
  }

  return data;
};

/**
 * POST /orden-pago/lotes/:id/aprobar
 * Aprueba de una sola vez todas las OP del lote.
 */
export const aprobarLoteOrdenPago = async (idLote, { user } = {}) => {
  const id = Number(idLote);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("id de lote inválido");
  }

  const body = {};
  const userTrim = String(user || "").trim();
  if (userTrim) body.user = userTrim;

  const res = await fetch(`${UrlApi}/orden-pago/lotes/${id}/aprobar`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));

  if (!res.ok || data.ok === false) {
    const error = new Error(
      data.msg || data.message || data.error || "No se pudo aprobar el lote"
    );
    error.status = res.status;
    error.codigo = data.codigo ?? null;
    throw error;
  }

  return data;
};

/**
 * POST /orden-pago/lotes/:id/anular
 * Rechaza el lote completo y libera los comprobantes. Body: { user, motivo? }
 */
export const anularLoteOrdenPago = async (idLote, { user, motivo } = {}) => {
  const id = Number(idLote);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("id de lote inválido");
  }

  const body = {};
  const userTrim = String(user || "").trim();
  if (userTrim) body.user = userTrim;
  const motivoTrim = String(motivo || "").trim();
  if (motivoTrim) body.motivo = motivoTrim;

  const res = await fetch(`${UrlApi}/orden-pago/lotes/${id}/anular`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));

  if (!res.ok || data.ok === false) {
    const error = new Error(
      data.msg || data.message || data.error || "No se pudo anular el lote"
    );
    error.status = res.status;
    error.codigo = data.codigo ?? null;
    throw error;
  }

  return data;
};

/**
 * GET /orden-pago
 * Listado unificado: filas mixtas `tipo: "lote" | "op"`.
 * - lote: fila padre con `ordenes_pago[]` (desplegable)
 * - op: pago suelto (sin lote)
 * `ordenes_pago` en la raíz es alias de `items`. La paginación cuenta filas
 * de primer nivel (1 lote = 1 fila).
 */
export const getOrdenesPago = async (params = {}) => {
  const q = new URLSearchParams();

  if (params.estado) q.set("estado", params.estado);
  const cuitFiltro = params.cuit || params.proveedor_cuit;
  if (cuitFiltro) q.set("cuit", normalizarCuit(cuitFiltro));
  if (params.fecha_desde) q.set("fecha_desde", params.fecha_desde);
  if (params.fecha_hasta) q.set("fecha_hasta", params.fecha_hasta);
  if (params.nro_op) q.set("nro_op", String(params.nro_op).trim());
  if (params.id_lote != null && params.id_lote !== "") {
    q.set("id_lote", String(params.id_lote));
  }
  if (params.nro_lote) q.set("nro_lote", String(params.nro_lote).trim());
  if (params.id_empresa != null && params.id_empresa !== "") {
    q.set("id_empresa", String(params.id_empresa));
  }
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));

  const query = q.toString();
  const url = query ? `${UrlApi}/orden-pago?${query}` : `${UrlApi}/orden-pago`;
  const res = await fetch(url);
  const data = await res.json();

  if (!res.ok || data.ok === false) {
    throw new Error(
      data.message || data.error || "No se pudieron obtener las órdenes de pago"
    );
  }

  return data;
};

export const getOrdenPagoById = async (idOrdenPago) => {
  const id = Number(idOrdenPago);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("id de orden de pago inválido");
  }

  const res = await fetch(`${UrlApi}/orden-pago/${id}`);
  const data = await res.json();

  if (!res.ok || data.ok === false) {
    throw new Error(
      data.msg || data.message || data.error || "No se pudo obtener la orden de pago"
    );
  }

  return data;
};

/**
 * POST /orden-pago/:idOrdenPago/aprobar
 * pendiente_aprobacion → generada
 */
export const aprobarOrdenPago = async (idOrdenPago, { user } = {}) => {
  const id = Number(idOrdenPago);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("id de orden de pago inválido");
  }

  const body = {};
  const userTrim = String(user || "").trim();
  if (userTrim) body.user = userTrim;

  const res = await fetch(`${UrlApi}/orden-pago/${id}/aprobar`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json();

  if (!res.ok || data.ok === false) {
    throw new Error(
      data.msg || data.message || data.error || "No se pudo aprobar la orden de pago"
    );
  }

  return data;
};

/**
 * POST /api/orden-pago/:id/anular
 * Libera facturas/reservas. Body: { user, motivo? }
 * Códigos: op_conciliada, anticipo_con_aplicaciones; ya_anulada → éxito silencioso.
 */
export const anularOrdenPago = async (idOrdenPago, { user, motivo } = {}) => {
  const id = Number(idOrdenPago);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("id de orden de pago inválido");
  }

  const body = {};
  const userTrim = String(user || "").trim();
  if (userTrim) body.user = userTrim;
  const motivoTrim = String(motivo || "").trim();
  if (motivoTrim) body.motivo = motivoTrim;

  const res = await fetch(`${UrlApi}/orden-pago/${id}/anular`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));

  if (!res.ok || data.ok === false) {
    const error = new Error(
      data.msg || data.message || data.error || "No se pudo anular la orden de pago"
    );
    error.status = res.status;
    error.codigo = data.codigo ?? null;
    throw error;
  }

  return data;
};

const parseFilenameDescarga = (contentDisposition, fallback) => {
  if (!contentDisposition) return fallback;
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/"/g, ""));
    } catch {
      /* ignore */
    }
  }
  const match = contentDisposition.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
};

/**
 * GET /orden-pago/:id/documentos/descargar
 * tipos: completo|todo, interbanking|interbanking_banco, op|op_pdf, retenciones|retenciones_zip, retencion_pdf (+ id_retencion)
 */
export const descargarDocumentoOrdenPago = async (
  idOrdenPago,
  { tipo, idRetencion } = {}
) => {
  const id = Number(idOrdenPago);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("id de orden de pago inválido");
  }
  if (!tipo) {
    throw new Error("tipo de documento requerido");
  }

  const params = new URLSearchParams({ tipo: String(tipo) });
  if (idRetencion != null && idRetencion !== "") {
    params.set("id_retencion", String(idRetencion));
  }

  const res = await fetch(
    `${UrlApi}/orden-pago/${id}/documentos/descargar?${params.toString()}`
  );

  if (!res.ok) {
    let msg = "No se pudo descargar el documento";
    try {
      const data = await res.json();
      msg = data.msg || data.message || data.error || msg;
    } catch {
      /* respuesta binaria o vacía */
    }
    throw new Error(msg);
  }

  const blob = await res.blob();
  const fallback =
    tipo === "completo" || tipo === "todo"
      ? `OrdenPago_${id}_completo.zip`
      : tipo === "retenciones" || tipo === "retenciones_zip"
        ? `OrdenPago_${id}_retenciones.zip`
        : tipo === "interbanking" || tipo === "interbanking_banco"
          ? `OrdenPago_${id}_interbanking.txt`
          : tipo === "retencion_pdf"
            ? `Retencion_${idRetencion}.pdf`
            : `OrdenPago_${id}.pdf`;

  const filename = parseFilenameDescarga(
    res.headers.get("Content-Disposition"),
    fallback
  );

  return { blob, filename };
};

export const getBancosCuentasActivos = async () => {
  const res = await fetch(`${UrlApi}/bancos_cuentas/activos`);
  if (!res.ok) {
    throw new Error("No se pudieron obtener las cuentas bancarias");
  }
  const data = await res.json();
  return Array.isArray(data) ? data : [];
};

/**
 * Saldo a favor del cliente con el mismo CUIT (compensación CC cliente ↔ proveedor).
 * Preferencia: GET /orden-pago/proveedores/:cuit/compensacion
 * Fallback: cliente por CUIT + saldo de recibos ctacte.
 */
export const getSaldoCompensacionProveedor = async (cuit) => {
  const cuitNorm = normalizarCuit(cuit);
  if (!cuitNorm) {
    throw new Error("Ingrese un CUIT válido");
  }

  const resDedicado = await fetch(
    `${UrlApi}/orden-pago/proveedores/${cuitNorm}/compensacion`
  );
  if (resDedicado.ok) {
    const data = await resDedicado.json();
    if (data?.ok === false) {
      throw new Error(
        data.message || data.error || "No se pudo obtener el saldo de compensación"
      );
    }
    return {
      id_cliente: data.id_cliente ?? data.idCliente ?? null,
      saldo_disponible: Number(
        data.saldo_disponible ?? data.saldo_a_favor ?? data.saldo ?? 0
      ),
      razon_social: data.razon_social ?? data.razonSocial ?? null,
    };
  }

  const resCli = await fetch(`${UrlApi}/clientes/existecuit/${cuitNorm}`);
  if (!resCli.ok) {
    throw new Error("No se pudo consultar si el proveedor es cliente");
  }
  const coincidencias = await resCli.json();
  const lista = Array.isArray(coincidencias)
    ? coincidencias
    : coincidencias
      ? [coincidencias]
      : [];
  const cliente = lista[0];
  if (!cliente) {
    return {
      id_cliente: null,
      saldo_disponible: 0,
      razon_social: null,
      mensaje: "No hay un cliente con el mismo CUIT para compensar",
    };
  }

  const idCliente = cliente.id ?? cliente.id_cliente ?? cliente.idCliente;
  const idTipo =
    cliente.id_tipocliente ??
    cliente.id_tipo_negocio ??
    cliente.id_tipo_cliente ??
    1;

  let saldoDisponible = 0;
  if (idCliente != null) {
    try {
      const resSaldo = await fetch(
        `${UrlApi}/ctacte/recibos/saldo/${idCliente}/${idTipo}`
      );
      if (resSaldo.ok) {
        const dataSaldo = await resSaldo.json();
        if (Array.isArray(dataSaldo)) {
          saldoDisponible = dataSaldo.reduce(
            (acc, r) =>
              acc +
              (Number(r.saldo ?? r.saldo_pendiente ?? r.importe_disponible) || 0),
            0
          );
        } else if (dataSaldo && typeof dataSaldo === "object") {
          saldoDisponible = Number(
            dataSaldo.saldo_disponible ??
              dataSaldo.saldo_a_favor ??
              dataSaldo.saldo ??
              0
          );
        }
      }
    } catch {
      saldoDisponible = 0;
    }
  }

  return {
    id_cliente: idCliente ?? null,
    saldo_disponible: Math.round(saldoDisponible * 100) / 100,
    razon_social: cliente.razon_social ?? cliente.razonSocial ?? null,
  };
};

export const getProveedorByCuit = async (cuit) => {
  const cuitNorm = normalizarCuit(cuit);
  const res = await fetch(`${UrlApi}/proveedores/bycuit/${cuitNorm}`);
  if (!res.ok) {
    throw new Error("No se pudieron obtener los datos del proveedor");
  }
  const rows = await res.json();
  return Array.isArray(rows) ? rows[0] : rows;
};

export const getProveedorLedger = async (cuit, { orden = "DESC" } = {}) => {
  const cuitNorm = normalizarCuit(cuit);
  if (!cuitNorm) {
    throw new Error("Ingrese un CUIT válido");
  }

  const params = new URLSearchParams({ orden });
  const res = await fetch(`${UrlApi}/proveedores/ledger/${cuitNorm}?${params}`);
  const data = await res.json();

  if (!res.ok || data.ok === false) {
    throw new Error(
      data.message || data.error || "No se pudo obtener la cuenta corriente del proveedor"
    );
  }

  return data;
};

export const getConciliacionPendientes = async () => {
  const res = await fetch(`${UrlApi}/orden-pago/conciliacion/pendientes`);
  const data = await res.json();

  if (!res.ok || data.ok === false) {
    throw new Error(
      data.msg || data.message || data.error || "No se pudieron obtener las OP pendientes"
    );
  }

  return data;
};

export const getNotificacionesOrdenPago = async (idOrdenPago) => {
  const id = Number(idOrdenPago);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("id de orden de pago inválido");
  }

  const res = await fetch(`${UrlApi}/orden-pago/${id}/notificaciones`);
  const data = await res.json();

  if (!res.ok || data.ok === false) {
    throw new Error(
      data.message || data.error || "No se pudo obtener el historial de envíos"
    );
  }

  return data;
};

export const reenviarMailOrdenPago = async (idOrdenPago, { email, user } = {}) => {
  const id = Number(idOrdenPago);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("id de orden de pago inválido");
  }

  const body = { user: String(user || "").trim() };
  const emailTrim = String(email || "").trim();
  if (emailTrim) {
    body.email = emailTrim;
  }

  const res = await fetch(`${UrlApi}/orden-pago/${id}/reenviar-mail`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json();

  if (!res.ok || data.ok === false) {
    throw new Error(
      data.msg || data.message || data.error || "No se pudo reenviar el correo"
    );
  }

  return data;
};

export const uploadConciliacionOrdenPago = async (file, confirmar = false) => {
  if (!file) {
    throw new Error("Seleccione un archivo CSV");
  }

  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(
    `${UrlApi}/orden-pago/conciliacion/upload?confirmar=${confirmar ? 1 : 0}`,
    {
      method: "POST",
      body: formData,
    }
  );
  const data = await res.json();

  if (!res.ok || data.ok === false) {
    throw new Error(
      data.msg || data.message || data.error || "No se pudo procesar el archivo de conciliación"
    );
  }

  return data;
};
